import React, { useEffect, useSyncExternalStore } from "react";

import { maintenance, startRecovery, confirmReadiness } from "utils/maintenance";
import { Maintenance } from "pages/Maintenance";

/**
 * Wraps the real app so a genuine API outage swaps in the maintenance page
 * without unmounting anything underneath it.
 *
 * The app stays mounted (just hidden) rather than being torn down: unmounting
 * <Routes /> would drop in-flight navigation/router state, and remounting it
 * on recovery would replay the entire app boot sequence (redux store init,
 * React Query cache, Statsig/Datadog setup already done once in
 * src/index.js) for no reason. `hidden` is used instead of conditionally
 * rendering `null`, per the same logic.
 *
 * `utils/maintenance` is a plain subscribable store (not redux/context) so it
 * can be written to from anywhere outage detection needs to happen —
 * currently `utils/requestMethodsBase.js`, several static-method levels away
 * from any component tree.
 */
const MaintenanceGate = ({ children }) => {
  const down = useSyncExternalStore(maintenance.subscribe, maintenance.getSnapshot);

  // Recovery polling only runs while the page is actually showing, so it
  // costs nothing when the app is healthy.
  useEffect(() => {
    if (!down) return undefined;
    return startRecovery(confirmReadiness);
  }, [down]);

  return (
    <>
      {down && <Maintenance />}
      <div hidden={down}>{children}</div>
    </>
  );
};

export default MaintenanceGate;
